/**
 * Analytics tracking utility for pushing events to Google Tag Manager dataLayer
 */

import { sha256 } from "./sha256";

// Types
interface SplitUPNResult {
  userPart: string;
  domain: string;
}

interface ExtractedNames {
  firstName: string;
  lastName: string;
}

interface PowerAutomatePayload {
  hashedEmail: string;
  member_id_part1: string;
  firstName: string;
  lastName: string;
  UPN: string;
  objectId: string;
  auth_method: string;
  timestamp: string;
}

// Constants
const POWER_AUTOMATE_API_URL =
  "https://e616baf0264f4a529a13d02ca17fc2.41.environment.api.powerplatform.com/powerautomate/automations/direct/workflows/e752cc4eca5f4315b1de9c3c717b9aca/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=Lmt5mR7NCXU-mOW42PUaXbqtERl9zo2RhgegTaYVY-I";
const AUTH_METHOD = "microsoft";

/**
 * Split UPN into user part and domain for analytics tracking
 */
const splitUPN = (email: string): SplitUPNResult => {
  if (!email?.includes("@")) {
    return { userPart: "", domain: "" };
  }

  const [userPart, domain] = email.split("@");
  return { userPart, domain };
};

/**
 * Extract first name and last name from email
 */
const extractNames = (email: string): ExtractedNames => {
  if (!email?.includes("@")) {
    return { firstName: "", lastName: "" };
  }

  const userPart = email.split("@")[0];
  const nameParts = userPart.split(".");

  const capitalizeFirstLetter = (str: string): string =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  if (nameParts.length >= 2) {
    return {
      firstName: capitalizeFirstLetter(nameParts[0]),
      lastName: capitalizeFirstLetter(nameParts[1]),
    };
  }

  if (nameParts.length === 1) {
    return {
      firstName: capitalizeFirstLetter(nameParts[0]),
      lastName: "",
    };
  }

  return { firstName: "", lastName: "" };
};

/**
 * Push an event to the dataLayer with essential user tracking data
 */
export const pushToDataLayer = async (
  eventName: string,
  userEmail: string,
  eventParams: Record<string, any> = {}
): Promise<void> => {
  if (typeof window === "undefined" || !window.dataLayer || !userEmail) {
    return;
  }
  const { userPart } = splitUPN(userEmail);
  const hashedEmail = await sha256(userEmail);

  window.dataLayer.push({
    event: eventName,
    member_id: hashedEmail,
    member_id_part1: userPart,
    UPN: userEmail,
    activity_date: new Date().toISOString(),
    ...eventParams,
  });
};

/**
 * Send hashed email to Power Automate API
 */
const sendHashedEmailToAPI = async (
  userEmail: string,
  userId?: string
): Promise<void> => {
  const { userPart } = splitUPN(userEmail);
  const { firstName, lastName } = extractNames(userEmail);

  try {
    const payload: PowerAutomatePayload = {
      hashedEmail: await sha256(userEmail),
      member_id_part1: userPart,
      firstName,
      lastName,
      UPN: userEmail,
      objectId: userId || "",
      auth_method: AUTH_METHOD,
      timestamp: new Date().toISOString(),
    };

    const response = await fetch(POWER_AUTOMATE_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      // Optional: Add timeout (10 seconds)
      signal: AbortSignal.timeout(10000),
    });

    if (!response.ok) {
      console.error(
        "Failed to send hashed email to API:",
        response.status,
        response.statusText
      );
      // Optionally log the response body for debugging
      const errorText = await response
        .text()
        .catch(() => "Unable to read error response");
      console.error("API Error Response:", errorText);
    }
  } catch (error) {
    console.error("Error sending hashed email to API:", error);
    // You could add retry logic here if needed
  }
};

/**
 * Track user login event - called when user profile is successfully loaded
 */
export const trackLogin = async (
  userEmail: string,
  contact: any
): Promise<void> => {
  // Check if login event already tracked this session
  if (sessionStorage.getItem("loginEventTracked")) {
    return;
  }

  if (!userEmail) {
    return;
  }

  const userId = contact?.id || "";

  // Send to GTM dataLayer
  await pushToDataLayer("user_login", userEmail, {
    auth_method: AUTH_METHOD,
    objectId: userId,
  });

  // Send to Power Automate API
  await sendHashedEmailToAPI(userEmail, userId);

  // Mark as tracked to prevent duplicate events
  sessionStorage.setItem("loginEventTracked", "true");
};
/**
 * Track user logout events
 */
export const trackLogout = async (userEmail: string): Promise<void> => {
  await pushToDataLayer("user_logout", userEmail);

  // Clear login event tracking for next session
  sessionStorage.removeItem("loginEventTracked");

  // Clear SSO session tracking
  const sessionKey = `sso_tracked_${await sha256(userEmail)}`;
  sessionStorage.removeItem(sessionKey);
};

/**
 * Track resource file access
 */
export const trackFileAccess = async (
  userEmail: string,
  fileUrl: string,
  operation = "viewed",
  userId?: string
): Promise<void> => {
  // Send to GTM dataLayer
  await pushToDataLayer("file_access", userEmail, {
    operation,
    file_accessed: fileUrl,
    objectId: userId || "",
    file_url_part1: fileUrl.substring(0, 100),
    file_url_part2: fileUrl.substring(100, 200),
    file_url_part3: fileUrl.substring(200, 300),
    file_url_part4: fileUrl.substring(300, 400),
  });
};
